import { isEqual } from "lodash-es";
import type { DetailFilter } from "./picture.ts";

const maxStrength = 5;
const maxRadiusRatio = 0.008;
const numDetailValues = 10;

const availableDetailValues = Array.from(
	{ length: numDetailValues },
	(_, i) => {
		const ratio = (i + 1) / numDetailValues;
		return {
			strength: ratio * maxStrength,
			radiusRatio: ratio * maxRadiusRatio,
		};
	},
);

const legacyDetailFilterOptions = [
	{ value: { sigma: 60, radiusRatio: 0.025 }, label: "Low", newIndex: 1 },
	{
		value: {
			sigma: 100,
			radiusRatio: 0.0444,
		},
		label: "Medium",
		newIndex: 2,
	},
	{
		value: {
			sigma: 160,
			radiusRatio: 0.08,
		},
		label: "High",
		newIndex: 4,
	},
];

function isLegacyDetailFilter(
	detailFilter: DetailFilter,
): detailFilter is Exclude<DetailFilter, { readonly strength: number }> {
	return "sigma" in detailFilter && typeof detailFilter.sigma === "number";
}

function findClosestDetailFilterValueIndex(value: DetailFilter) {
	if (isLegacyDetailFilter(value)) {
		const previousOption = legacyDetailFilterOptions.find((o) =>
			isEqual(o.value, value),
		);
		if (previousOption !== undefined) {
			return previousOption.newIndex;
		}

		// Non-standard legacy detail value
		const previousMaxSigma = Math.max(
			...legacyDetailFilterOptions.map((o) => o.value.sigma),
		);
		const ratio = Math.min(value.sigma / previousMaxSigma, 1);
		return Math.min(
			Math.floor(availableDetailValues.length * ratio),
			availableDetailValues.length - 1,
		);
	}

	const radiusRatioWeight = maxStrength / maxRadiusRatio;
	let minimum:
		| { readonly index: number; readonly difference: number }
		| undefined;
	for (let i = 0; i < availableDetailValues.length; i++) {
		const availableValue = availableDetailValues[i];
		const difference =
			Math.abs(value.strength - availableValue.strength) +
			radiusRatioWeight *
				Math.abs(value.radiusRatio - availableValue.radiusRatio);
		if (minimum === undefined || minimum.difference > difference) {
			minimum = {
				difference,
				index: i,
			};
		}
	}

	if (minimum === undefined) {
		throw new Error("Invalid state - no minimum");
	}
	return minimum.index;
}

export {
	isLegacyDetailFilter,
	availableDetailValues,
	legacyDetailFilterOptions,
	findClosestDetailFilterValueIndex,
};
